// Ubicación en el repo: src/lib/patientAttachments.js
// Documentos adjuntos de un paciente — tabla `patient_attachments` + bucket
// privado `patient-attachments` de Supabase Storage. Las dos cosas ya
// existían desde la migración inicial (0001 tabla, 0003 RLS de la tabla,
// 0004 bucket + políticas de storage.objects) — DECISIONS.md #20 solo
// dejaba pendiente construir esta capa de la app, nunca hizo falta SQL
// nuevo. Mismo contenido que addPatientAttachment()/patientAttachListHTML()
// del prototipo (línea ~1634-1676), pero subiendo el archivo real a Storage
// en vez de guardarlo como base64 en la fila del paciente.
//
// Convención de ruta exigida por la política de storage.objects:
// `{patient_id}/{nombre único}` — patient_branch_match() lee el patient_id
// del primer segmento de la ruta. El bucket es privado, así que "Ver" pide
// una signed URL de corta duración en vez de una URL pública.
import { supabase } from './supabaseClient';
import { compressImageIfNeeded } from './imageCompression';

const BUCKET = 'patient-attachments';
// Mismo límite fijado en el bucket (migración 0014) — se valida acá antes
// de subir para dar un mensaje claro en vez del error genérico de Storage,
// y para no gastar ancho de banda subiendo algo que el bucket va a
// rechazar de todas formas.
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

export async function listPatientAttachments(patientId) {
  const { data, error } = await supabase
    .from('patient_attachments')
    .select('id, name, mime, storage_path, uploaded_at')
    .eq('patient_id', patientId)
    .is('deleted_at', null)
    .order('uploaded_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function uploadPatientAttachment(patientId, file, actorId) {
  // Comprimir primero (si aplica) — el chequeo de tamaño corre sobre lo que
  // realmente se va a subir, no sobre el original.
  const toUpload = await compressImageIfNeeded(file);

  if (toUpload.size > MAX_FILE_SIZE_BYTES) {
    const mb = (toUpload.size / (1024 * 1024)).toFixed(1);
    throw new Error(`El archivo pesa ${mb} MB — el máximo permitido es 10 MB.`);
  }
  const safeName = toUpload.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
  const path = `${patientId}/${crypto.randomUUID()}-${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(path, toUpload, { contentType: toUpload.type || undefined });
  if (uploadError) throw uploadError;

  // El nombre visible en la lista es el original (el que reconoce quien lo
  // subió), aunque internamente se haya recomprimido a .jpg.
  const { error: insertError } = await supabase.from('patient_attachments').insert({
    patient_id: patientId,
    name: file.name,
    mime: toUpload.type || null,
    storage_path: path,
    uploaded_by: actorId,
  });
  if (insertError) {
    // No dejar el archivo huérfano en Storage si no se pudo guardar la
    // metadata (ej. la fila cayó por RLS pero el archivo ya se subió).
    await supabase.storage.from(BUCKET).remove([path]);
    throw insertError;
  }
}

// URL firmada de corta duración (el bucket es privado) — se pide recién al
// hacer clic en "Ver", no al listar.
export async function getAttachmentUrl(storagePath) {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(storagePath, 60);
  if (error) throw error;
  return data.signedUrl;
}

// Baja lógica, mismo criterio que el resto de la app (ver DECISIONS.md #2):
// nunca se borra el archivo real de Storage, solo se marca la fila de
// metadata como eliminada — conserva trazabilidad para Ley 21.719.
export async function softDeletePatientAttachment(id, actorId) {
  const { error } = await supabase
    .from('patient_attachments')
    .update({ deleted_at: new Date().toISOString(), deleted_by: actorId })
    .eq('id', id);
  if (error) throw error;
}
